import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'

import {
  createAdminApplication,
  downloadApplicationPdf,
  downloadBlankAgreementPdf,
  downloadPaperIndemnity,
  downloadPaymentProof,
  fetchAdminApplications,
  fetchMarkets,
  formatMarketDateOption,
  updateApplicationStatus,
  uploadPaperIndemnity,
  uploadPaymentProof,
  type Application,
  type Market,
} from '@/api/applicationsApi'
import { ApiClientError } from '@/api/client'
import { fetchAdminVendors, type VendorAccount } from '@/api/vendorsApi'
import { useAuth } from '@/auth/AuthContext'
import { AdminHeaderActions } from '@/components/admin/AdminHeaderActions'

import authStyles from './AuthPages.module.css'
import styles from './PortalPages.module.css'

function BtnIcon({ children }: { children: ReactNode }) {
  return (
    <span className={styles.btnIcon} aria-hidden="true">
      {children}
    </span>
  )
}

/** Playful market-day stroke icons for admin action buttons. */
const Icons = {
  open: (
    <svg viewBox="0 0 24 24">
      <path d="M4 19V7.5A2.5 2.5 0 0 1 6.5 5H14l6 5.5V19a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1Z" />
      <path d="M14 5v5h6" />
      <path d="M8 13h8M8 16h5" />
    </svg>
  ),
  pdf: (
    <svg viewBox="0 0 24 24">
      <path d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
      <path d="M14 3v5h5" />
      <path d="M8.5 14.5c.8-2 1.8-3 3.2-3 1 0 1.5.6 1.5 1.4 0 2.2-3.2 2.4-3.2 4.1V18" />
      <path d="M15.5 11.5V18" />
    </svg>
  ),
  upload: (
    <svg viewBox="0 0 24 24">
      <path d="M12 16V7" />
      <path d="m8.5 10.5 3.5-3.5 3.5 3.5" />
      <path d="M5 19h14" />
      <path d="M7 19v-3M17 19v-3" />
    </svg>
  ),
  download: (
    <svg viewBox="0 0 24 24">
      <path d="M12 7v9" />
      <path d="m8.5 12.5 3.5 3.5 3.5-3.5" />
      <path d="M5 19h14" />
    </svg>
  ),
  approve: (
    <svg viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="8.5" />
      <path d="m8.5 12.2 2.4 2.4 4.6-5.2" />
    </svg>
  ),
  reject: (
    <svg viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="8.5" />
      <path d="m9 9 6 6M15 9l-6 6" />
    </svg>
  ),
} as const

function todayIso(): string {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function marketDate(app: Application): string {
  return (app.market_event_date || '').slice(0, 10)
}

function compareName(a: Application, b: Application): number {
  const byName = a.applicant_name.localeCompare(b.applicant_name, undefined, {
    sensitivity: 'base',
  })
  if (byName !== 0) return byName
  const byBusiness = a.business_name.localeCompare(b.business_name, undefined, {
    sensitivity: 'base',
  })
  if (byBusiness !== 0) return byBusiness
  return a.id - b.id
}

function compareUpcoming(a: Application, b: Application): number {
  const aDate = marketDate(a)
  const bDate = marketDate(b)
  if (aDate !== bDate) {
    if (!aDate) return 1
    if (!bDate) return -1
    return aDate.localeCompare(bDate)
  }
  return compareName(a, b)
}

function comparePast(a: Application, b: Application): number {
  const byDate = marketDate(b).localeCompare(marketDate(a))
  if (byDate !== 0) return byDate
  return compareName(a, b)
}

type PaperForm = {
  user_id: number | ''
  market_id: number | ''
  applicant_name: string
  business_name: string
  email: string
  cellphone: string
  items_for_sale: string
  vendor_category: 'commercial' | 'school_community' | 'learner_classroom'
  supervising_teacher: string
  identity_number: string
  stall_number: string
  electricity_needed: boolean
  signatory_name: string
  signed_on: string
}

const EMPTY_PAPER_FORM: PaperForm = {
  user_id: '',
  market_id: '',
  applicant_name: '',
  business_name: '',
  email: '',
  cellphone: '',
  items_for_sale: '',
  vendor_category: 'commercial',
  supervising_teacher: '',
  identity_number: '',
  stall_number: '',
  electricity_needed: false,
  signatory_name: '',
  signed_on: todayIso(),
}

type UploadKind = 'pop' | 'indemnity'

/** Admin list/manage vendor indemnity applications. */
export function AdminApplicationsPage() {
  const { isAuthenticated, isAdmin, logout } = useAuth()
  const navigate = useNavigate()
  const [applications, setApplications] = useState<Application[]>([])
  const [vendors, setVendors] = useState<VendorAccount[]>([])
  const [markets, setMarkets] = useState<Market[]>([])
  const [error, setError] = useState('')
  const [saved, setSaved] = useState('')
  const [busyId, setBusyId] = useState<number | null>(null)
  const [uploadTargetId, setUploadTargetId] = useState<number | null>(null)
  const [uploadKind, setUploadKind] = useState<UploadKind>('pop')
  const [showPast, setShowPast] = useState(false)
  const [showPaperForm, setShowPaperForm] = useState(false)
  const [paperForm, setPaperForm] = useState<PaperForm>(EMPTY_PAPER_FORM)
  const [paperBusy, setPaperBusy] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  async function reload() {
    const result = await fetchAdminApplications()
    setApplications(result.applications)
  }

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) return
    void (async () => {
      try {
        const [appsResult, vendorsResult, marketsResult] = await Promise.all([
          fetchAdminApplications(),
          fetchAdminVendors(),
          fetchMarkets(),
        ])
        setApplications(appsResult.applications)
        setVendors(vendorsResult.vendors)
        setMarkets(marketsResult.markets)
      } catch (err) {
        setError(err instanceof ApiClientError ? err.message : 'Could not load applications.')
      }
    })()
  }, [isAuthenticated, isAdmin])

  const { upcoming, past } = useMemo(() => {
    const today = todayIso()
    const upcomingApps = applications
      .filter((app) => {
        const date = marketDate(app)
        return !date || date >= today
      })
      .sort(compareUpcoming)
    const pastApps = applications
      .filter((app) => {
        const date = marketDate(app)
        return Boolean(date) && date < today
      })
      .sort(comparePast)
    return { upcoming: upcomingApps, past: pastApps }
  }, [applications])

  const vendorOptions = useMemo(
    () =>
      [...vendors].sort((a, b) =>
        a.full_name.localeCompare(b.full_name, undefined, { sensitivity: 'base' }),
      ),
    [vendors],
  )

  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (!isAdmin) return <Navigate to="/vendor" replace />

  function updatePaperField<K extends keyof PaperForm>(key: K, value: PaperForm[K]) {
    setPaperForm((current) => ({ ...current, [key]: value }))
    setSaved('')
  }

  function onVendorPicked(vendorId: number | '') {
    if (vendorId === '') {
      updatePaperField('user_id', '')
      return
    }
    const vendor = vendors.find((row) => row.id === vendorId)
    setPaperForm((current) => ({
      ...current,
      user_id: vendorId,
      applicant_name: vendor?.full_name || current.applicant_name,
      business_name: vendor?.business_name || current.business_name,
      email: vendor?.email || current.email,
      cellphone: vendor?.whatsapp_number || current.cellphone,
      signatory_name: vendor?.full_name || current.signatory_name,
    }))
    setSaved('')
  }

  function startPaperForm() {
    setShowPaperForm(true)
    setPaperForm({
      ...EMPTY_PAPER_FORM,
      market_id: markets[0]?.id ?? '',
      signed_on: todayIso(),
    })
    setError('')
    setSaved('')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  async function onCreatePaper(event: FormEvent) {
    event.preventDefault()
    if (paperForm.user_id === '' || paperForm.market_id === '') {
      setError('Select a vendor and market date.')
      return
    }
    if (
      paperForm.vendor_category === 'learner_classroom' &&
      !paperForm.supervising_teacher.trim()
    ) {
      setError('Supervising teacher is required for learner / classroom markets.')
      return
    }
    setPaperBusy(true)
    setError('')
    setSaved('')
    try {
      const result = await createAdminApplication({
        user_id: paperForm.user_id,
        market_id: paperForm.market_id,
        applicant_name: paperForm.applicant_name,
        business_name: paperForm.business_name,
        email: paperForm.email,
        cellphone: paperForm.cellphone,
        items_for_sale: paperForm.items_for_sale,
        vendor_category: paperForm.vendor_category,
        supervising_teacher:
          paperForm.vendor_category === 'learner_classroom'
            ? paperForm.supervising_teacher
            : null,
        identity_number: paperForm.identity_number || null,
        stall_number: paperForm.stall_number || null,
        electricity_needed: paperForm.electricity_needed,
        signatory_name: paperForm.signatory_name,
        signed_on: paperForm.signed_on,
      })
      setShowPaperForm(false)
      setPaperForm(EMPTY_PAPER_FORM)
      await reload()
      navigate(`/admin/applications/${result.application.id}`)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not create paper application.')
    } finally {
      setPaperBusy(false)
    }
  }

  function renderApplicationList(items: Application[]) {
    return items.map((app) => (
      <article key={app.id} className={styles.item}>
        <div>
          <h2 className={styles.itemTitle}>
            {app.applicant_name} · {app.business_name}
          </h2>
          <p className={styles.meta}>
            {app.market_title}
            {app.market_event_date ? ` · ${app.market_event_date}` : ''}
            {' · '}
            {app.email} · {app.cellphone}
            {app.whatsapp_number ? ` · WA ${app.whatsapp_number}` : ''}
          </p>
          <p className={styles.meta}>
            Status:{' '}
            <strong
              className={
                app.status === 'approved'
                  ? styles.metaOk
                  : app.status === 'rejected'
                    ? styles.metaAlert
                    : undefined
              }
            >
              {app.status}
            </strong>
            {app.fee_amount != null ? ` · R${app.fee_amount.toFixed(2)}` : ''}
            {app.electricity_needed ? ' · electricity' : ''}
          </p>
          <p className={styles.meta}>
            Indemnity:{' '}
            {app.has_signature ? (
              <strong className={styles.metaOk}>digital signature</strong>
            ) : app.has_paper_indemnity ? (
              <strong className={styles.metaOk}>
                paper
                {app.paper_indemnity_original_name
                  ? ` · ${app.paper_indemnity_original_name}`
                  : ''}
              </strong>
            ) : (
              <strong className={styles.metaAlert}>missing</strong>
            )}
            {' · '}
            POP:{' '}
            {app.has_payment_proof ? (
              <strong className={styles.metaOk}>
                uploaded
                {app.payment_proof_original_name
                  ? ` · ${app.payment_proof_original_name}`
                  : ''}
              </strong>
            ) : (
              <strong className={styles.metaAlert}>missing</strong>
            )}
            {' · '}
            Content:{' '}
            <strong className={app.content_approved ? styles.metaOk : styles.metaAlert}>
              {app.content_approved ? 'approved' : 'not approved'}
            </strong>
          </p>
          <p className={styles.meta}>
            Ready to approve:{' '}
            <strong className={app.can_approve ? styles.metaOk : styles.metaAlert}>
              {app.can_approve ? 'yes' : 'no'}
            </strong>
            {!app.can_approve
              ? ` · needs${[
                  app.is_form_complete === false ? ' complete form' : '',
                  app.has_indemnity === false ? ' signature or paper indemnity' : '',
                  app.has_payment_proof ? '' : ' POP',
                ]
                  .filter(Boolean)
                  .join(',')}`
              : ''}
          </p>
          <p className={styles.meta}>Items: {app.items_for_sale}</p>
        </div>
        <div className={styles.actionCol}>
          <Link className={styles.secondary} to={`/admin/applications/${app.id}`}>
            <BtnIcon>{Icons.open}</BtnIcon>
            Open
          </Link>
          <button
            type="button"
            className={styles.secondary}
            disabled={busyId === app.id}
            onClick={() => void onDownloadPdf(app)}
          >
            <BtnIcon>{Icons.pdf}</BtnIcon>
            PDF
          </button>
          <button
            type="button"
            className={styles.primary}
            disabled={busyId === app.id}
            onClick={() => openUploadPicker(app.id, 'indemnity')}
          >
            <BtnIcon>{Icons.upload}</BtnIcon>
            {app.has_paper_indemnity ? 'Replace paper indemnity' : 'Upload paper indemnity'}
          </button>
          {app.has_paper_indemnity ? (
            <button
              type="button"
              className={styles.secondary}
              disabled={busyId === app.id}
              onClick={() => void onDownloadIndemnity(app)}
            >
              <BtnIcon>{Icons.download}</BtnIcon>
              Download paper indemnity
            </button>
          ) : null}
          <button
            type="button"
            className={styles.primary}
            disabled={busyId === app.id}
            onClick={() => openUploadPicker(app.id, 'pop')}
          >
            <BtnIcon>{Icons.upload}</BtnIcon>
            {app.has_payment_proof ? 'Replace POP' : 'Upload POP'}
          </button>
          {app.has_payment_proof ? (
            <button
              type="button"
              className={styles.secondary}
              disabled={busyId === app.id}
              onClick={() => void onDownloadPop(app)}
            >
              <BtnIcon>{Icons.download}</BtnIcon>
              Download POP
            </button>
          ) : null}
          <button
            type="button"
            className={styles.secondary}
            disabled={busyId === app.id || !app.can_approve || app.status === 'approved'}
            title={
              app.status === 'approved'
                ? 'Already approved'
                : app.can_approve
                  ? 'Approve stall'
                  : 'Requires complete form, signature or paper indemnity, and proof of payment'
            }
            onClick={() => void onStatus(app.id, 'approved')}
          >
            <BtnIcon>{Icons.approve}</BtnIcon>
            Approve
          </button>
          <button
            type="button"
            className={styles.danger}
            disabled={busyId === app.id}
            onClick={() => void onStatus(app.id, 'rejected')}
          >
            <BtnIcon>{Icons.reject}</BtnIcon>
            Reject
          </button>
        </div>
      </article>
    ))
  }

  async function onStatus(id: number, status: 'approved' | 'rejected' | 'submitted') {
    setBusyId(id)
    setError('')
    try {
      await updateApplicationStatus(id, { status })
      await reload()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Update failed.')
    } finally {
      setBusyId(null)
    }
  }

  async function onDownloadPdf(app: Application) {
    setBusyId(app.id)
    setError('')
    try {
      await downloadApplicationPdf(app)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'PDF download failed.')
    } finally {
      setBusyId(null)
    }
  }

  async function onDownloadPop(app: Application) {
    setBusyId(app.id)
    setError('')
    try {
      await downloadPaymentProof(app)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Proof of payment download failed.')
    } finally {
      setBusyId(null)
    }
  }

  async function onDownloadIndemnity(app: Application) {
    setBusyId(app.id)
    setError('')
    try {
      await downloadPaperIndemnity(app)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Paper indemnity download failed.')
    } finally {
      setBusyId(null)
    }
  }

  function openUploadPicker(id: number, kind: UploadKind) {
    setUploadTargetId(id)
    setUploadKind(kind)
    setError('')
    fileInputRef.current?.click()
  }

  async function onFileSelected(fileList: FileList | null) {
    const applicationId = uploadTargetId
    const kind = uploadKind
    const file = fileList?.[0]
    setUploadTargetId(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (!applicationId || !file) return

    setBusyId(applicationId)
    setError('')
    try {
      if (kind === 'indemnity') {
        await uploadPaperIndemnity(applicationId, file)
      } else {
        await uploadPaymentProof(applicationId, file)
      }
      await reload()
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.message
          : kind === 'indemnity'
            ? 'Paper indemnity upload failed.'
            : 'Proof of payment upload failed.',
      )
    } finally {
      setBusyId(null)
    }
  }

  async function onDownloadBlankForm() {
    setError('')
    try {
      await downloadBlankAgreementPdf()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Blank form download failed.')
    }
  }

  return (
    <section className={styles.page}>
      <header className={styles.header}>
        <div>
          <p className={styles.brand}>Oikos Market Day</p>
          <h1 className={styles.title}>Admin · Applications</h1>
          <p className={styles.lead}>
            Review stall applications, capture paper bookings, upload paper indemnity scans or proof of
            payment, and update status.
          </p>
        </div>
        <AdminHeaderActions logout={logout} />
      </header>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
        hidden
        onChange={(event) => void onFileSelected(event.target.files)}
      />

      {error ? <p className={styles.error}>{error}</p> : null}
      {saved ? <p className={styles.metaOk}>{saved}</p> : null}

      {showPaperForm ? (
        <form className={styles.formGrid} onSubmit={(event) => void onCreatePaper(event)}>
          <h2 className={styles.itemTitle}>New paper application</h2>
          <p className={styles.meta}>
            Capture a stall booking from a paper form. After saving, upload the scanned indemnity and POP
            on the application detail page (or from this list).
          </p>

          <label className={authStyles.label}>
            Vendor account
            <select
              className={authStyles.select}
              value={paperForm.user_id === '' ? '' : String(paperForm.user_id)}
              onChange={(e) =>
                onVendorPicked(e.target.value ? Number(e.target.value) : '')
              }
              required
            >
              <option value="">Select vendor…</option>
              {vendorOptions.map((vendor) => (
                <option key={vendor.id} value={vendor.id}>
                  {vendor.full_name}
                  {vendor.business_name ? ` · ${vendor.business_name}` : ''}
                  {!vendor.is_active ? ' (inactive)' : ''}
                </option>
              ))}
            </select>
          </label>

          <label className={authStyles.label}>
            Market date
            <select
              className={authStyles.select}
              value={paperForm.market_id === '' ? '' : String(paperForm.market_id)}
              onChange={(e) =>
                updatePaperField('market_id', e.target.value ? Number(e.target.value) : '')
              }
              required
            >
              <option value="">Select market…</option>
              {markets.map((market) => (
                <option key={market.id} value={market.id}>
                  {formatMarketDateOption(market)}
                </option>
              ))}
            </select>
          </label>

          <label className={authStyles.label}>
            Applicant name
            <input
              className={authStyles.input}
              value={paperForm.applicant_name}
              onChange={(e) => updatePaperField('applicant_name', e.target.value)}
              required
            />
          </label>
          <label className={authStyles.label}>
            Business name
            <input
              className={authStyles.input}
              value={paperForm.business_name}
              onChange={(e) => updatePaperField('business_name', e.target.value)}
              required
            />
          </label>
          <label className={authStyles.label}>
            Email
            <input
              className={authStyles.input}
              type="email"
              value={paperForm.email}
              onChange={(e) => updatePaperField('email', e.target.value)}
              required
            />
          </label>
          <label className={authStyles.label}>
            Cellphone
            <input
              className={authStyles.input}
              value={paperForm.cellphone}
              onChange={(e) => updatePaperField('cellphone', e.target.value)}
              required
            />
          </label>
          <label className={authStyles.label}>
            Vendor category
            <select
              className={authStyles.select}
              value={paperForm.vendor_category}
              onChange={(e) =>
                updatePaperField(
                  'vendor_category',
                  e.target.value as PaperForm['vendor_category'],
                )
              }
              required
            >
              <option value="commercial">Commercial Vendor</option>
              <option value="school_community">School Community Vendor</option>
              <option value="learner_classroom">Learner / Classroom Market</option>
            </select>
          </label>
          {paperForm.vendor_category === 'learner_classroom' ? (
            <label className={authStyles.label}>
              Supervising teacher
              <input
                className={authStyles.input}
                value={paperForm.supervising_teacher}
                onChange={(e) => updatePaperField('supervising_teacher', e.target.value)}
                required
              />
            </label>
          ) : null}
          <label className={authStyles.label}>
            Identity / registration number (optional)
            <input
              className={authStyles.input}
              value={paperForm.identity_number}
              onChange={(e) => updatePaperField('identity_number', e.target.value)}
            />
          </label>
          <label className={authStyles.label}>
            Stall number (optional)
            <input
              className={authStyles.input}
              value={paperForm.stall_number}
              onChange={(e) => updatePaperField('stall_number', e.target.value)}
            />
          </label>
          <label className={authStyles.label}>
            Items / products for sale
            <textarea
              className={authStyles.textarea}
              value={paperForm.items_for_sale}
              onChange={(e) => updatePaperField('items_for_sale', e.target.value)}
              required
            />
          </label>
          <label className={styles.check}>
            <input
              type="checkbox"
              checked={paperForm.electricity_needed}
              onChange={(e) => updatePaperField('electricity_needed', e.target.checked)}
            />
            Electricity needed
          </label>
          <label className={authStyles.label}>
            Signatory name
            <input
              className={authStyles.input}
              value={paperForm.signatory_name}
              onChange={(e) => updatePaperField('signatory_name', e.target.value)}
              required
            />
          </label>
          <label className={authStyles.label}>
            Signed on
            <input
              className={authStyles.input}
              type="date"
              value={paperForm.signed_on}
              onChange={(e) => updatePaperField('signed_on', e.target.value)}
              required
            />
          </label>

          <div className={styles.actions}>
            <button className={authStyles.submit} type="submit" disabled={paperBusy}>
              {paperBusy ? 'Creating…' : 'Create paper application'}
            </button>
            <button
              type="button"
              className={styles.ghost}
              disabled={paperBusy}
              onClick={() => {
                setShowPaperForm(false)
                setPaperForm(EMPTY_PAPER_FORM)
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      <div className={styles.list} style={{ marginTop: showPaperForm ? '1.5rem' : 0 }}>
        <div className={styles.header} style={{ marginBottom: '0.75rem' }}>
          <h2 className={styles.itemTitle}>Upcoming markets</h2>
          <div className={styles.actions}>
            <button type="button" className={styles.secondary} onClick={() => void onDownloadBlankForm()}>
              Download blank form
            </button>
            <button type="button" className={styles.primary} onClick={startPaperForm}>
              New paper application
            </button>
            <button
              type="button"
              className={styles.secondary}
              onClick={() => setShowPast((current) => !current)}
            >
              {showPast ? 'Hide past markets' : `List past markets (${past.length})`}
            </button>
          </div>
        </div>

        {upcoming.length === 0 ? (
          <p className={styles.empty}>No upcoming applications.</p>
        ) : (
          renderApplicationList(upcoming)
        )}

        {showPast ? (
          <>
            <h2 className={styles.itemTitle} style={{ marginTop: '1.5rem' }}>
              Past markets
            </h2>
            {past.length === 0 ? (
              <p className={styles.empty}>No past applications.</p>
            ) : (
              renderApplicationList(past)
            )}
          </>
        ) : null}
      </div>
    </section>
  )
}
